import { Injectable } from '@angular/core';
import { ProductsControllerApiService } from '@lx/shared/api/services';
import { Language } from '../models/language.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProductPackage, ProductPackageEnum } from '@lx/quote-tool/shared/types';
import { BaseService, CachedRequestResponse } from '@lx/shared/utils-http';

@Injectable({
  providedIn: 'root',
})
export class LanguageService extends BaseService<Language> {
  private productPackage$ = new BehaviorSubject<ProductPackage | 'default'>('default');

  private responseCaches = {
    default: new CachedRequestResponse<Language[]>(() => this.productsApiService.getCourseLanguages(this.locale, this.companyHeader), this.cacheSettings.get),
    [ProductPackageEnum.YOUNG_LEARNER_CAMPS]: new CachedRequestResponse<Language[]>(
      () => this.productsApiService.getCourseLanguages(this.locale, this.companyHeader, ProductPackageEnum.YOUNG_LEARNER_CAMPS),
      this.cacheSettings.get,
    ),
  };

  constructor(private productsApiService: ProductsControllerApiService) {
    super();
  }

  /**
   * @deprecated Use getByProductPackage() instead. This method will be removed in future versions.
   * Changing product package as a side effect is not recommended.
   */
  changeProductPackage(packageType: ProductPackage | null | undefined): void {
    this.productPackage$.next(packageType ? packageType : 'default');
  }

  /**
   * Gets all languages for a specific product package.
   * This is the preferred method as it doesn't have side effects on the service state.
   *
   * @example old way (stateful, two calls):
   * this.languageService.changeProductPackage(ProductPackageEnum.YOUNG_LEARNER_CAMPS);
   * this.languageService.getAll();
   *
   * @param productPackage - The product package to filter languages (e.g., YOUNG_LEARNER_CAMPS for Junior flow)
   * @returns Observable<Language[]> - Languages available for the specified product package
   */
  getByProductPackage(productPackage: ProductPackage | null | undefined): Observable<Language[]> {
    if (!productPackage) {
      return this.responseCaches.default.get();
    }

    // Use existing cache if available for this product package
    if (this.hasCache(productPackage)) {
      return this.responseCaches[productPackage].get();
    }

    // For product packages without a dedicated cache, create one dynamically
    // This ensures the API is called with the correct productPackage filter
    this.responseCaches[productPackage] = new CachedRequestResponse<Language[]>(
      () => this.productsApiService.getCourseLanguages(this.locale, this.companyHeader, productPackage),
      this.cacheSettings.get,
    );

    return this.responseCaches[productPackage].get();
  }

  getByCode(code: string): Observable<Language> {
    return this.getAll().pipe(map((languages) => languages.find((l) => l.code === code)));
  }

  protected getResponseCache$(): Observable<CachedRequestResponse<Language[]>> {
    return this.productPackage$.pipe(map((productPackage) => this.responseCaches[productPackage]));
  }

  /**
   * Checks if a cache exists for the given product package.
   * @param productPackage - The product package to check
   * @returns boolean - True if a cache exists for this product package
   */
  private hasCache(productPackage: ProductPackage): boolean {
    return productPackage in this.responseCaches;
  }
}
